import { WarningOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { accents } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import { FilterIssueKind } from 'features/trend-detection/constants/stale-filters'
import type {
  FilterCriterion,
  AgentStaleFilter,
} from 'features/trend-detection/types'

/** Rendered in place of a display name when a theme's uuid no longer resolves
 * to anything in the taxonomy. See `FilterIssueKind.UnresolvableTheme`. */
const DELETED_VALUE_LABEL = '[Deleted Value]'

/**
 * One removable target inside the notice. `value === null` drops the whole
 * criterion (unknown field / range misuse); otherwise only the one stored
 * value is dropped.
 */
export type StaleFilterRemoval = {
  field: string
  value: string | null
}

type Props = {
  staleFilters: readonly AgentStaleFilter[]
  /**
   * Current form criteria — cross-referenced against the server's staleness
   * verdict so a value the user has already removed disappears from the
   * notice without a refetch.
   */
  criteria?: readonly FilterCriterion[]
  isEditor: boolean
  /** Omitted for viewers — rows still render, without the Remove button. */
  onRemove?: (removal: StaleFilterRemoval) => void
}

/**
 * Actionable stale-filters notice — one row per stale (field, value) tuple.
 * Remove drops that value directly (the picker can't render values absent from
 * the current taxonomy).
 */
export const StaleFiltersNotice: React.FC<Props> = ({
  staleFilters,
  criteria = [],
  isEditor,
  onRemove,
}) => {
  // Union values by field — two criteria can share a `field` (LLM emits
  // `theme` + `parent_theme`), so `new Map(criteria.map(...))` would drop one.
  // Used to filter out values the user has already removed.
  const criteriaByField = new Map<string, Set<string>>()
  for (const c of criteria) {
    const set = criteriaByField.get(c.field) ?? new Set<string>()
    for (const v of c.values ?? []) set.add(v)
    criteriaByField.set(c.field, set)
  }
  const rows: Array<{
    id: string
    label: string
    removal: StaleFilterRemoval
  }> = []

  for (const issue of staleFilters) {
    // The picker is keyed by the CONFIG'S field name; `issue.field` is the
    // checker's LLM-facing name (e.g. "Dataset" for a config `proj_uuid`).
    // Fall back to `field` when the two happen to match (Brand, Product Brand).
    const configField = issue.stored_field ?? issue.field
    // Field-level shapes: the field itself is broken (not in taxonomy, or
    // used as a numeric range as a list). Whole criterion goes on Remove.
    if (
      issue.kind === FilterIssueKind.UnknownField ||
      issue.kind === FilterIssueKind.RangeFieldMisuse
    ) {
      if (!criteriaByField.has(configField)) continue
      rows.push({
        id: `field:${configField}`,
        label:
          issue.kind === FilterIssueKind.RangeFieldMisuse
            ? `${issue.field} — must be a numeric range, not a list`
            : `${issue.field} — no longer offered`,
        removal: { field: configField, value: null },
      })
      continue
    }
    // Value-level: match by STORED (uuid for themes/datasets, display for
    // simple fields). Missing label + UnresolvableTheme → "[Deleted Value]".
    const currentValues = criteriaByField.get(configField)
    if (!currentValues) continue
    const storedList =
      issue.stored_values && issue.stored_values.length > 0
        ? issue.stored_values
        : issue.invalid_values
    const isUnresolvableTheme = issue.kind === FilterIssueKind.UnresolvableTheme
    for (const stored of storedList) {
      if (!currentValues.has(stored)) continue
      const displayLabel = issue.stored_value_labels?.[stored]
      const label = displayLabel
        ? `${issue.field}: ${displayLabel}`
        : isUnresolvableTheme
          ? `${issue.field}: ${DELETED_VALUE_LABEL}`
          : `${issue.field}: ${stored}`
      rows.push({
        id: `value:${configField}:${stored}`,
        label,
        removal: { field: configField, value: stored },
      })
    }
  }

  if (rows.length === 0) return null

  return (
    <Section role="status">
      <Label>
        <WarningOutlined /> No longer available
      </Label>
      {rows.map((row) => (
        <Row key={row.id}>
          <RowLabel>{row.label}</RowLabel>
          {isEditor && onRemove && (
            <RemoveButton
              type="button"
              aria-label={`Remove ${row.label}`}
              onClick={() => onRemove(row.removal)}
            >
              Remove
            </RemoveButton>
          )}
        </Row>
      ))}
      {!isEditor && (
        <ViewerHint>Ask an editor of this agent to fix these.</ViewerHint>
      )}
    </Section>
  )
}

// Understated container that groups the label + rows as one visual unit.
// Palette matches sutra's Chiclet `warning` tone (goldenLight 100/400/800),
// so this notice and the "Filters need attention" chip on the DetailHeader
// read as the same warning system across surfaces.
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid ${accents.goldenLight[400]};
  border-radius: 6px;
  background: ${accents.goldenLight[100]};
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${accents.goldenLight[800]};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding-left: ${spacing[3]}px;
`

const RowLabel = styled.span`
  font-size: 12px;
  color: ${accents.goldenLight[800]};
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${accents.goldenLight[800]};
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: ${accents.goldenLight[900]};
  }
`

const ViewerHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${accents.goldenLight[800]};
  opacity: 0.85;
`
